import React, { useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import getConfig from 'next/config';

import { eventBus } from '@atc/cai-event-bus';
import { useFeatures } from '@atc/features';

import {
    ConnectedMyWalletModal,
    ConnectedWalletPaymentDetailsModal,
    ownerDetailsFetcher,
    VrsServiceClient,
    WalletManagerContainer,
} from 'reaxl-wallet';

import {
    WALLET_VALIDATE_DEALS_BY_VIN_COMPLETED,
    WALLET_VALIDATE_DEALS_BY_VIN_REQUEST,
} from 'wallet-manager';

import currentPageNameDuck from '@/ducks/currentPageNameDuck.js';
import dealsDuck from '@/ducks/dealsDuck.js';
import interestRateDuck from '@/ducks/interestRateDuck.js';
import inventoryDuck from '@/ducks/inventoryDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';
import srpActiveInteractionDuck from '@/ducks/srp/srpActiveInteractionDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpReuseActiveStateDuck from '@/ducks/srp/srpReuseActiveStateDuck.js';
import userAgentDuck from '@/ducks/userAgentDuck.js';
import userDuck from '@/ducks/userDuck.js';
import vdpActiveInteractionDuck from '@/ducks/vdp/vdpActiveInteractionDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

// fetchers
import tradeInFetcher from '@/fetchers/ConsumerBrowserHistoryFetcher';
import creditVerificationFetcher from '@/fetchers/creditVerificationFetcher';
import EmailOwnerFetcher from '@/fetchers/EmailOwnerFetcher';
import IcoFetcher from '@/fetchers/IcoFetcher';
import PaymentCalculationFetcher from '@/fetchers/PaymentCalculationFetcher';
import VrsServiceFetcher from '@/fetchers/VrsServiceFetcher';

import useSrpNavigation from '@/hooks/useSrpNavigation';

const vrsServices = new VrsServiceClient({ vrsFetcher: VrsServiceFetcher });

function MyWalletContainer({
    blockTradeInFetch = false,
}) {
    const dispatch = useDispatch();

    const { visible: filtersVisible } = useSelector(srpFiltersDuck.selectors.getDuckState);
    const currentPageName = useSelector(currentPageNameDuck.selectors.getDuckState);
    const isBot = useSelector(userAgentDuck.selectors.isBot);

    const navigateToSrp = useSrpNavigation();

    const { publicRuntimeConfig } = getConfig();

    const {
        wallet_payment_option: [enablePaymentOption, { disable_lease: isLeaseDisabled }],
        wallet_email_capture: [enableWalletEmailCapture],
    } = useFeatures(['wallet_payment_option', 'wallet_email_capture']);

    const handleSearchByBudget = useCallback(async (maxPriceValue, walletPaymentOption) => {

        const newFiltersValues = {};

        if (maxPriceValue) {
            newFiltersValues.maxPrice = maxPriceValue;
        }

        if (enablePaymentOption && walletPaymentOption === 'lease') {
            newFiltersValues.listingTypes = 'NEW';
        }

        dispatch(srpActiveInteractionDuck.creators.setKeys({
            showEmailModal: false,
            showEmailConfirmationModal: false,
        }));

        // to ensure srpFiltersDuck get new maxPrice value
        await dispatch(srpReuseActiveStateDuck.creators.setFalse());
        navigateToSrp({
            filtersValues: newFiltersValues,
            isNewSearch: true,
            resetPagination: true,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigateToSrp, dispatch]);

    const onSacaAppClick = () => {
        dispatch(vdpActiveInteractionDuck.creators.setKeys({
            showFinanceAppModal: true,
        }));
    };

    const walletProps = {
        vdpResultsDuck,
        paymentsDuck,
        dealsDuck,
        userDuck,
        currentPageNameDuck,
        enableWalletEmailCapture,
        inventoryDuck,
        interestRateDuck,
        // disable duplicate cbh fetch from wallet on srp
        blockTradeInFetch,
        tradeInFetcher,
        IcoFetcher,
        vrsServicesFetcher: vrsServices,
        creditVerificationFetcher,
        EmailOwnerFetcher,
        paymentFetcher: PaymentCalculationFetcher,
        pageName: currentPageName,
        onSacaAppClick: () => onSacaAppClick(),
        onSearchByBudget: useCallback(async ({ maxPrice, walletPaymentOption }) => {
            await handleSearchByBudget(maxPrice, walletPaymentOption);
        }, [handleSearchByBudget]),
        onSearchAvailVehicles: useCallback(async () => {
            if (filtersVisible) {
                await Promise.resolve(dispatch(srpFiltersDuck.creators.hideFilters()));
            }
        }, [filtersVisible, dispatch]),
    };

    if (isBot) {
        delete walletProps.tradeInFetcher;
    }

    const appFetchers = {
        ownerDetailsFetcher: ownerDetailsFetcher(),
    };

    const validateDealsByVin = (vins = []) => {
        const vinsWithVdpHref = vins.reduce((acc, vin, key) => {
            const vinObject = { vin };

            acc.push(vinObject);

            return acc;
        }, []);
        eventBus.emit(WALLET_VALIDATE_DEALS_BY_VIN_COMPLETED, vinsWithVdpHref);
    };

    useEffect(() => {
        eventBus.on(WALLET_VALIDATE_DEALS_BY_VIN_REQUEST, validateDealsByVin);
        return () => {
            eventBus.off(WALLET_VALIDATE_DEALS_BY_VIN_REQUEST, validateDealsByVin);
        };
    }, []);

    return (
        <>
            <WalletManagerContainer
                paymentsDuck={paymentsDuck}
                env={publicRuntimeConfig?.environment === 'prod' ? 'prod' : 'staging'}
                fetchers={appFetchers}
                isLeaseDisabled={isLeaseDisabled}
            />
            <ConnectedMyWalletModal {...walletProps} />
            <ConnectedWalletPaymentDetailsModal
                paymentsDuck={paymentsDuck}
                vdpResultsDuck={currentPageName === 'vdp' && vdpResultsDuck}
            />
        </>
    );
}

export default MyWalletContainer;
